/**
 * DonutChart — interactive allocation donut with hover highlight.
 *
 * Phase 3 Task 3.3 (2026-05-17): decoupled from the fake-data/anna
 * `AllocationItem` shape. Accepts a generic `DonutSlice` so the dashboard
 * can pass slices derived from `useDashboardAllocation()` without the
 * fixture import.
 */

export interface DonutSlice {
  id: string;
  pct: number;
  color: string;
}

interface DonutChartProps {
  data: DonutSlice[];
  hoveredId: string | null;
  onHover: (id: string | null) => void;
}

export function DonutChart({ data, hoveredId, onHover }: DonutChartProps) {
  const size = 240;
  const stroke = 26;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  let cumulative = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {/* Background ring */}
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--bg-2)" strokeWidth={stroke} />
      {data.map((d) => {
        const len = (d.pct / 100) * circ;
        const offset = -cumulative;
        cumulative += len;
        const isOther = hoveredId !== null && hoveredId !== d.id;
        return (
          <circle
            key={d.id}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={stroke}
            strokeDasharray={`${len - 4} ${circ - len + 4}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${c} ${c})`}
            opacity={isOther ? 0.25 : 1}
            style={{ transition: 'opacity 240ms var(--ease-cushion)', cursor: 'pointer' }}
            strokeLinecap="butt"
            onMouseEnter={() => onHover(d.id)}
            onMouseLeave={() => onHover(null)}
          />
        );
      })}
    </svg>
  );
}
